/**
 * Account Updates
 *
 * Pure functions for updating account fields and deleting accounts.
 * Part of the accounts domain (split from accountHelpers.ts).
 */

import { AccountsState, RelationshipGraph, OnboardingStepState } from '../types'
import { error as logError } from '../../utils/logger'

/**
 * Update account display name
 */
export function updateAccountName(
  accountsState: AccountsState,
  accountId: string,
  newName: string,
): AccountsState {
  const account = accountsState.accountsById[accountId]
  if (!account) {
    logError(`Account not found: ${accountId}`)
    return accountsState
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [accountId]: {
        ...account,
        displayName: newName,
        lastActive: Date.now(),
      },
    },
  }
}

/**
 * Update account email
 */
export function updateAccountEmail(
  accountsState: AccountsState,
  accountId: string,
  email: string,
): AccountsState {
  const account = accountsState.accountsById[accountId]
  if (!account) {
    return accountsState
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [accountId]: {
        ...account,
        email,
        lastActive: Date.now(),
      },
    },
  }
}

/**
 * Update account password hash
 */
export function updateAccountPassword(
  accountsState: AccountsState,
  accountId: string,
  newPasswordHash: string,
): AccountsState {
  const account = accountsState.accountsById[accountId]
  if (!account) {
    logError(`Account not found: ${accountId}`)
    return accountsState
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [accountId]: {
        ...account,
        passwordHash: newPasswordHash,
        lastActive: Date.now(),
      },
    },
  }
}

/**
 * Update teacher details
 */
export function updateTeacherDetails(
  accountsState: AccountsState,
  teacherId: string,
  updates: { schoolName?: string },
): AccountsState {
  const account = accountsState.accountsById[teacherId]
  if (!account || account.role !== 'teacher') {
    return accountsState
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [teacherId]: {
        ...account,
        ...updates,
        lastActive: Date.now(),
      },
    },
  }
}

/**
 * Update learner link code
 */
export function updateLearnerLinkCode(
  accountsState: AccountsState,
  learnerId: string,
  linkCode: string,
): AccountsState {
  const account = accountsState.accountsById[learnerId]
  if (!account || account.role !== 'learner') {
    return accountsState
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [learnerId]: {
        ...account,
        linkCode,
        lastActive: Date.now(),
      },
    },
  }
}

/**
 * Update learner grade
 */
export function updateLearnerGrade(
  accountsState: AccountsState,
  learnerId: string,
  grade: number,
): AccountsState {
  const account = accountsState.accountsById[learnerId]
  if (!account || account.role !== 'learner') {
    return accountsState
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [learnerId]: {
        ...account,
        grade,
        lastActive: Date.now(),
      },
    },
  }
}

/**
 * Update learner-specific details (grade, username).
 * Pure function — returns updated AccountsState.
 */
export function updateLearnerDetails(
  accountsState: AccountsState,
  learnerId: string,
  updates: { grade?: number; username?: string },
): AccountsState {
  const account = accountsState.accountsById[learnerId]
  if (!account || account.role !== 'learner') {
    return accountsState
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [learnerId]: {
        ...account,
        ...updates,
        lastActive: Date.now(),
      },
    },
  }
}

/**
 * Touch lastActive timestamp on an account (for session keep-alive).
 * Pure function — returns updated AccountsState.
 */
export function touchLastActive(
  state: AccountsState,
  accountId: string,
): AccountsState {
  const account = state.accountsById[accountId]
  if (!account) return state

  const now = Date.now()
  // Debounce updates to prevent excessive re-renders (only update if > 1 min ago)
  if (now - account.lastActive < 60000) {
    return state
  }

  return {
    ...state,
    accountsById: {
      ...state.accountsById,
      [accountId]: {
        ...account,
        lastActive: now,
      },
    },
  }
}

/**
 * Delete an account from the accounts state, cleaning up all relationships.
 * Pure function — returns updated AccountsState.
 * Returns unchanged state if trying to delete the last admin.
 *
 * ⚠️ IMPORTANT: This function only operates on AccountsState (accounts + relationships).
 * It does NOT clean up `learnerData` (progress, mastery, XP, etc.) for deleted learners.
 *
 * **Do NOT call this directly** — use `accountActions.deleteAccount()` from
 * `useAccountActions` instead, which wraps this function and also removes
 * the learner's data from `learnerData[accountId]`.
 *
 * @see useAccountActions.deleteAccount — safe wrapper that handles full cleanup
 * @param accountsState - The current accounts state (accounts + relationships)
 * @param accountId - The ID of the account to delete
 * @returns Updated AccountsState with account removed and relationships cleaned up,
 *          or unchanged state if account not found or is the last admin.
 */
export function deleteAccountFromState(
  accountsState: AccountsState,
  accountId: string,
): AccountsState {
  const account = accountsState.accountsById[accountId]
  if (!account) return accountsState

  // Don't allow deleting the last admin
  if (account.role === 'admin') {
    const adminCount = Object.values(accountsState.accountsById).filter(
      (a) => a.role === 'admin',
    ).length
    if (adminCount <= 1) return accountsState
  }

  const newAccountsById = { ...accountsState.accountsById }
  delete newAccountsById[accountId]

  // Clean up relationships
  const newRelationships: RelationshipGraph = {
    parentToLearners: { ...accountsState.relationships.parentToLearners },
    learnerToParents: { ...accountsState.relationships.learnerToParents },
    teacherToLearners: { ...accountsState.relationships.teacherToLearners },
    learnerToTeachers: { ...accountsState.relationships.learnerToTeachers },
  }

  if (account.role === 'parent') {
    delete newRelationships.parentToLearners[accountId]
    Object.keys(newRelationships.learnerToParents).forEach((lId) => {
      newRelationships.learnerToParents[lId] =
        newRelationships.learnerToParents[lId].filter((id) => id !== accountId)
    })
  } else if (account.role === 'teacher') {
    // Remove teacher's forward mapping
    const linkedLearners = newRelationships.teacherToLearners[accountId] || []
    delete newRelationships.teacherToLearners[accountId]
    // Remove teacher from all linked learners' reverse mapping (O(k) where k = linked learners)
    linkedLearners.forEach((lId) => {
      if (newRelationships.learnerToTeachers[lId]) {
        newRelationships.learnerToTeachers[lId] =
          newRelationships.learnerToTeachers[lId].filter(
            (id) => id !== accountId,
          )
      }
    })
  } else if (account.role === 'learner') {
    delete newRelationships.learnerToParents[accountId]
    delete newRelationships.learnerToTeachers[accountId]
    Object.keys(newRelationships.parentToLearners).forEach((pId) => {
      newRelationships.parentToLearners[pId] =
        newRelationships.parentToLearners[pId].filter((id) => id !== accountId)
    })
    // Use learnerToTeachers for efficient cleanup instead of iterating all teachers
    const linkedTeachers =
      accountsState.relationships.learnerToTeachers?.[accountId] || []
    linkedTeachers.forEach((tId) => {
      if (newRelationships.teacherToLearners[tId]) {
        newRelationships.teacherToLearners[tId] =
          newRelationships.teacherToLearners[tId].filter(
            (id) => id !== accountId,
          )
      }
    })
  }

  return {
    ...accountsState,
    accountsById: newAccountsById,
    relationships: newRelationships,
    // If active account was deleted, switch to the first available one
    activeAccountId:
      accountsState.activeAccountId === accountId
        ? Object.keys(newAccountsById)[0] || ''
        : accountsState.activeAccountId,
  }
}

/**
 * Mark an account's onboarding as completed.
 * Sets hasCompletedOnboarding = true on the specified account.
 * Also saves the granular step state if provided.
 */
export function completeOnboarding(
  accountsState: AccountsState,
  accountId: string,
  stepState?: OnboardingStepState,
): AccountsState {
  const account = accountsState.accountsById[accountId]
  if (!account) return accountsState
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [accountId]: {
        ...account,
        hasCompletedOnboarding: true,
        ...(stepState ? { onboardingSteps: stepState } : {}),
      },
    },
  }
}
